/**
 * Employee self-service profile — the data an EMPLOYEE owns about themselves.
 *
 * ── Why this is a separate model from User/SimpleUser ─────────────────────────────────────
 *
 * User/SimpleUser is the ORGANISATIONAL record: who you report to, what you are paid to do,
 * when you joined, which shift you work. HR owns every field on it, and the requirement is
 * explicit that an employee must not be able to change any of them.
 *
 * The safest way to guarantee that is not a permission check on a shared record — it is for the
 * two to be different records. Nothing the employee submits is written anywhere near the
 * organisational fields, so "the employee overwrote their own manager" is not a bug that can be
 * introduced later by a careless merge. The HR view reads both and displays them together; the
 * write paths never meet.
 *
 * Keyed by canonical email, like every other per-employee store in this application, so the
 * domain migration (see services/identity.ts) does not split a person's profile in half.
 */

/** Where an employee is in the self-service flow. HR sees this on the Employee Information list. */
export enum ProfileCompletionStatus {
  /** Created, no invitation sent. HR is filling everything in themselves. */
  PENDING = 'PENDING',
  /** An invitation email has gone out and the link is live. */
  INVITATION_SENT = 'INVITATION_SENT',
  /** The employee has saved at least once but not submitted. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** The employee has submitted, and HR has not yet ruled on it. */
  SUBMITTED = 'SUBMITTED',
  /**
   * Approved once, and the employee has since proposed further edits.
   *
   * Distinct from SUBMITTED because the profile is USABLE in this state — every approved value
   * is still current and payroll can rely on it. SUBMITTED means nothing has been ratified yet.
   * Collapsing the two would have HR treating a complete profile with one pending phone number
   * as though none of it had been checked.
   */
  CHANGES_PENDING_APPROVAL = 'CHANGES_PENDING_APPROVAL',
  /** Required information submitted AND approved by HR. */
  COMPLETED = 'COMPLETED',
}

export const PROFILE_STATUS_LABEL: Record<ProfileCompletionStatus, string> = {
  [ProfileCompletionStatus.PENDING]: 'Pending',
  [ProfileCompletionStatus.INVITATION_SENT]: 'Invitation sent',
  [ProfileCompletionStatus.IN_PROGRESS]: 'In progress',
  [ProfileCompletionStatus.SUBMITTED]: 'Submitted',
  [ProfileCompletionStatus.CHANGES_PENDING_APPROVAL]: 'Changes pending approval',
  [ProfileCompletionStatus.COMPLETED]: 'Completed',
};

/** Where one proposed field change stands. */
export enum ChangeRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  /** HR wants it resubmitted with a correction. Distinct from rejected: the ask is still open. */
  CORRECTION_REQUESTED = 'CORRECTION_REQUESTED',
}

export const CHANGE_STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  [ChangeRequestStatus.PENDING]: 'Pending approval',
  [ChangeRequestStatus.APPROVED]: 'Approved',
  [ChangeRequestStatus.REJECTED]: 'Rejected',
  [ChangeRequestStatus.CORRECTION_REQUESTED]: 'Correction requested',
};

/**
 * One proposed change to one field, and everything needed to rule on it.
 *
 * ── Why changes are per FIELD and not per submission ──────────────────────────────────────
 *
 * HR rules on facts, not on forms. An employee who moves house and mistypes their PAN in the
 * same sitting should have the address applied and the PAN queried — a submission-level
 * approve/reject forces HR to reject both and makes the employee re-enter the correct one.
 *
 * It also makes the audit trail answer the question people actually ask, which is "when did this
 * value change and who agreed to it", not "which batch was it in".
 *
 * ── path ──────────────────────────────────────────────────────────────────────────────────
 *
 * A dotted path into the profile — "personal.dateOfBirth", "address.permanent.city". Rows in a
 * repeating section are addressed by their stable id rather than their index:
 * "qualifications.<id>.institute". An index would mean deleting an earlier row silently
 * re-points every pending change below it at the wrong record.
 */
export interface ProfileChangeRequest {
  id: string;
  employeeEmail: string;
  section: string;
  path: string;
  /** Human label for the field, so HR reads "Date of birth" rather than a path. */
  label: string;
  /** The approved value at the time the change was proposed. Null when the field was empty. */
  oldValue: string | null;
  newValue: string | null;
  submittedBy: string;
  /** EMPLOYEE or HR. An HR edit is recorded here too, already approved — see the workflow. */
  submittedByRole: 'EMPLOYEE' | 'HR';
  submittedAt: string;
  status: ChangeRequestStatus;
  decidedBy?: string;
  decidedAt?: string;
  /** Required when rejecting. The employee sees it and corrects against it. */
  decisionReason?: string;
  /** A supporting document the employee attached to justify this change. */
  documentKey?: string;
}

// ─── Section 2: Personal Information ────────────────────────────────────────────────────

export interface PersonalInformation {
  dateOfBirth?: string;
  gender?: string;
  bloodGroup?: string;
  personalEmail?: string;
  mobileNumber?: string;
  fathersName?: string;
  mothersName?: string;
  maritalStatus?: string;
  marriageDate?: string;
  spouseName?: string;
  nationality?: string;
}

// ─── Section 3: Address ─────────────────────────────────────────────────────────────────

export interface PostalAddress {
  line1?: string;
  line2?: string;
  city?: string;
  district?: string;
  state?: string;
  country?: string;
  pincode?: string;
}

export interface AddressSection {
  permanent?: PostalAddress;
  present?: PostalAddress;
  /** Set when the employee ticks "same as permanent" — kept so the intent survives a re-edit. */
  presentSameAsPermanent?: boolean;
}

// ─── Section 4: Emergency Contact ───────────────────────────────────────────────────────

export interface EmergencyContact {
  name?: string;
  mobileNumber?: string;
  relationship?: string;
}

// ─── Sections 5, 6, 8, 9: repeating rows ────────────────────────────────────────────────

/**
 * Rows carry a client-generated `id`.
 *
 * Not an index: React keys off it, and a list where a deleted middle row shifts every key below
 * it re-mounts inputs and loses focus mid-typing. It is also what the document attachments hang
 * off, so a row's files survive the row moving.
 */
export interface Qualification {
  id: string;
  qualification?: string;
  institute?: string;
  fromYear?: string;
  toYear?: string;
  grade?: string;
  documentKeys?: string[];
}

export interface PreviousEmployment {
  id: string;
  companyName?: string;
  designation?: string;
  fromDate?: string;
  toDate?: string;
  lastDrawnSalary?: string;
  reasonForLeaving?: string;
  companyAddress?: string;
  documentKeys?: string[];
}

export interface FamilyMember {
  id: string;
  name?: string;
  relationship?: string;
  dateOfBirth?: string;
  occupation?: string;
  isDependent?: boolean;
}

export interface Nomination {
  id: string;
  nomineeName?: string;
  relationship?: string;
  dateOfBirth?: string;
  /** Share of the benefit, 0-100. Validated to total 100 across rows when any row is present. */
  sharePercentage?: string;
  /** What the nomination is against — PF, gratuity, insurance. */
  nominationFor?: string;
}

// ─── Section 7: Identifications ─────────────────────────────────────────────────────────

export interface Identifications {
  /** 12 digits — validated by utils/identityDocs. Masked in every list view. */
  aadhaarNumber?: string;
  /** ABCDE1234F — validated by utils/identityDocs. */
  panNumber?: string;
  passportNumber?: string;
  passportExpiry?: string;
  drivingLicence?: string;
  /**
   * The Aadhaar and PAN card scans, each in its own slot so submit can require both.
   *
   * One shared list could only prove that SOME card was attached. `documentKeys` stays for the
   * optional ones (passport, driving licence) and for scans filed before the split.
   */
  aadhaarDocumentKeys?: string[];
  panDocumentKeys?: string[];
  documentKeys?: string[];
}

// ─── Section 10: Accounts & Statutory ───────────────────────────────────────────────────

export interface AccountsStatutory {
  bankName?: string;
  branch?: string;
  accountNumber?: string;
  ifscCode?: string;
  nameAsPerBank?: string;
  pfNumber?: string;
  uanNumber?: string;
  esiNumber?: string;
  documentKeys?: string[];
}

// ─── The whole thing ────────────────────────────────────────────────────────────────────

/**
 * Everything an employee submits, plus the flow state around it.
 *
 * Every section is optional at the type level. The employee saves progressively and a
 * half-filled profile is a legitimate stored state (IN_PROGRESS) — requiring completeness in the
 * type would mean the draft could not be persisted at all.
 */
/**
 * The APPROVED values — what the organisation currently believes about this employee.
 *
 * Split out from the record so that "approved data" is a thing the type system knows about
 * rather than a convention. Anything reading employee information reads this; pending proposals
 * live beside it and can never be mistaken for it, which is the requirement that a pending
 * change must never display as approved information.
 */
export interface ApprovedProfileData {
  profilePhotoKey?: string;
  personal?: PersonalInformation;
  address?: AddressSection;
  emergencyContact?: EmergencyContact;
  qualifications?: Qualification[];
  previousEmployment?: PreviousEmployment[];
  identifications?: Identifications;
  family?: FamilyMember[];
  nominations?: Nomination[];
  accounts?: AccountsStatutory;
  documentKeys?: string[];
}

export interface EmployeeSelfServiceProfile extends ApprovedProfileData {
  /** Canonical email. The join key to the User record; never edited by the employee. */
  employeeEmail: string;
  status: ProfileCompletionStatus;

  /**
   * Proposals awaiting a decision. NOT applied to the fields above.
   *
   * The fields inherited from ApprovedProfileData stay at their approved values for as long as
   * a change sits here — so every existing reader of this type keeps seeing ratified data and
   * cannot accidentally surface a proposal, without any of them being changed.
   */
  pendingChanges?: ProfileChangeRequest[];

  /**
   * Every decided change, newest first. Approvals, rejections and HR's own edits.
   *
   * Kept on the profile rather than in a separate log because the question it answers — "why is
   * this value what it is" — is always asked while looking at the value.
   */
  history?: ProfileChangeRequest[];

  // The approved values themselves are inherited from ApprovedProfileData above.

  invitationSentAt?: string;
  invitationSentBy?: string;
  /** When the live link stops working. Absent means no live invitation. */
  invitationExpiresAt?: string;
  submittedAt?: string;
  updatedAt?: string;
}

/**
 * What the PUBLIC completion page is given after its token is validated.
 *
 * Deliberately NOT the User record. It carries the handful of HR fields the page shows read-only
 * — so the employee can see the context they are completing — and nothing else. Role, manager,
 * salary and status are absent by construction rather than by a filter that could be forgotten.
 */
export interface ProfileInvitationContext {
  employeeEmail: string;
  employeeName: string;
  employeeId?: string;
  designation?: string;
  department?: string;
  dateOfJoining?: string;
  expiresAt: string;
  profile: EmployeeSelfServiceProfile;
}
